import { useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { Icon } from '@clay-run/glaze/icon';
import { IconButton } from '@clay-run/glaze/iconbutton';
import { Text } from '@clay-run/glaze/text';
import { TextLink } from '@clay-run/glaze/textlink';

import { FLOW_ICONS } from '../../icons';
import { useCopiedFlash } from '../../../../hooks/useCopiedFlash';
import { isNested, nestedEntries, copyText, displayValue, URL_PATTERN, isLongText } from '../../EnrollQueue/payload-display';
import { looksLikeHtml, sanitizeHtml } from '../../../../lib/htmlContent';
import { Module } from '../ui/Module';

/**
 * FactModule — the V2 rail's payload renderer (U10), styled as a `Module`
 * (`fill="secondary"`) instead of V1's `FieldTiles` bordered-tile stack.
 * Same no-bracket-JSON contract as `FieldTiles` (EnrollQueue/FieldTiles.tsx):
 * a nested object/array collapses to a "N fields"/"N items" row that expands
 * its children indented underneath — pretty mode never `JSON.stringify`s a
 * nested value. `mode="raw"` is the escape hatch that DOES dump raw JSON,
 * for parity debugging against the V1 payload.
 *
 * Pure helpers (URL/HTML detection, nested-entry walk, `displayValue`,
 * `copyText`) now live in `EnrollQueue/payload-display.ts` (cleanup item b,
 * rail-glaze-parity QA round) — U10 originally had this file COPY them from
 * `FieldTiles.tsx` rather than share; this is the shared home that brief
 * anticipated. `tileIcon` is NOT shared — this component has no icon column.
 */

export type FactMode = 'pretty' | 'raw';

/** Presentation-only allowlist: no style/form/input/button/iframe/img/table; `href` is the only attribute. */
const PAYLOAD_HTML_CONFIG = {
  ALLOWED_TAGS: ['br', 'p', 'ul', 'ol', 'li', 'a', 'div', 'span', 'strong', 'em', 'b', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote'],
  ALLOWED_ATTR: ['href'],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
};

export interface FactModuleProps {
  title: string;
  data: unknown;
  mode?: FactMode;
  controls?: ReactNode;
  'data-testid'?: string;
}

export function FactModule({ title, data, mode = 'pretty', controls, 'data-testid': dataTestId }: FactModuleProps) {
  return (
    <Module fill="secondary" headerControl={Boolean(controls)} scrollFade={false} bodyScroll={false} data-testid={dataTestId}>
      <Module.Header controls={controls}>{title}</Module.Header>
      {mode === 'raw' ? (
        <pre className="overflow-x-auto rounded-md bg-bg-primary p-3 font-mono text-xs whitespace-pre">
          {JSON.stringify(data ?? null, null, 2)}
        </pre>
      ) : (
        <FactRows data={data} />
      )}
    </Module>
  );
}

export interface FactRowsProps {
  data: unknown;
}

export function FactRows({ data }: FactRowsProps) {
  if (data === null || data === undefined) {
    return <Text size="xs" variant="tertiary">No data</Text>;
  }

  if (!isNested(data)) {
    return <LeafRow fieldKey="value" value={data} isLast />;
  }

  const entries = nestedEntries(data);
  if (entries.length === 0) {
    return <Text size="xs" variant="tertiary">No data</Text>;
  }

  return (
    <div className="flex flex-col">
      {entries.map(([key, value], i) => {
        const isLast = i === entries.length - 1;
        return isNested(value) ? (
          <NestedRow key={key} fieldKey={key} value={value} isLast={isLast} />
        ) : (
          <LeafRow key={key} fieldKey={key} value={value} isLast={isLast} />
        );
      })}
    </div>
  );
}

function CopyButton({ fieldKey, value }: { fieldKey: string; value: unknown }) {
  const [copied, flashCopied] = useCopiedFlash(900);
  return (
    <IconButton
      variant="ghost"
      size="xs"
      icon={copied ? FLOW_ICONS.confirm : FLOW_ICONS.copy}
      aria-label={`Copy ${fieldKey}`}
      title={`Copy ${fieldKey}`}
      className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
      onClick={e => {
        e.stopPropagation();
        void navigator.clipboard?.writeText(copyText(value));
        flashCopied();
      }}
    />
  );
}

/**
 * Class lists for the two leaf layouts, written out in full (plain strings; no
 * tailwind-merge in this repo). HTML and long/multi-line text (and long URLs)
 * stack: label on its own line, value full-width and left-aligned beneath it.
 * Short scalars stay inline: label-left, value-right.
 */
const INLINE_LEAF = {
  row: 'group flex items-start justify-between gap-x-4 py-2',
  label: 'min-w-0 max-w-[40%] [overflow-wrap:anywhere]',
  value: 'flex min-w-0 flex-1 items-start justify-end gap-1',
  // Glaze's TextLink is `inline-flex shrink-0` and does not wrap. Put it in a `min-w-0 flex-1`
  // wrapper (so Copy keeps its own room inside the row) and cap the link to the wrapper; a long
  // unbroken URL breaks anywhere.
  urlWrap: 'min-w-0 flex-1 text-right',
  link: 'min-w-0 max-w-full text-right [overflow-wrap:anywhere]',
  text: 'min-w-0 whitespace-pre-wrap break-words text-right',
} as const;

const STACKED_LEAF = {
  row: 'group flex flex-col items-stretch gap-x-4 gap-y-1 py-2',
  label: 'min-w-0 [overflow-wrap:anywhere]',
  value: 'flex min-w-0 items-start justify-between gap-1',
  urlWrap: 'min-w-0 flex-1',
  link: 'min-w-0 max-w-full [overflow-wrap:anywhere]',
  text: 'min-w-0 flex-1 whitespace-pre-wrap break-words text-left',
} as const;

function LeafRow({ fieldKey, value, isLast }: { fieldKey: string; value: unknown; isLast: boolean }) {
  const isNull = value === null || value === undefined;
  const isUrl = typeof value === 'string' && URL_PATTERN.test(value);
  const isHtml = typeof value === 'string' && looksLikeHtml(value);
  const display = isNull ? 'null' : displayValue(value);
  const c = isHtml || (typeof value === 'string' && isLongText(value)) ? STACKED_LEAF : INLINE_LEAF;

  return (
    <div className={clsx(c.row, !isLast && 'border-b-[0.5px] border-border-primary')}>
      <Text size="xs" variant="tertiary" className={c.label}>{fieldKey}</Text>
      <div className={c.value}>
        {isUrl ? (
          <div className={c.urlWrap}>
            <TextLink href={value} target="_blank" rel="noreferrer" size="xs" className={c.link}>
              {display}
            </TextLink>
          </div>
        ) : isHtml ? (
          <div
            className="prose prose-sm min-w-0 max-w-none flex-1 text-left text-content-secondary [overflow-wrap:anywhere] [&_pre]:overflow-x-auto"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(value, PAYLOAD_HTML_CONFIG) }}
          />
        ) : (
          <Text size="xs" variant={isNull ? 'tertiary' : undefined} className={c.text}>
            {display}
          </Text>
        )}
        <CopyButton fieldKey={fieldKey} value={isNull ? null : value} />
      </div>
    </div>
  );
}

function NestedRow({
  fieldKey,
  value,
  isLast,
}: {
  fieldKey: string;
  value: Record<string, unknown> | unknown[];
  isLast: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const count = Array.isArray(value) ? value.length : Object.keys(value).length;
  const noun = Array.isArray(value) ? 'item' : 'field';
  const countLabel = `${count} ${noun}${count === 1 ? '' : 's'}`;

  return (
    <div>
      <div
        className={clsx(
          'group flex w-full items-center gap-2 py-2',
          !isLast && !expanded && 'border-b-[0.5px] border-border-primary',
        )}
      >
        {/* The toggle and the copy affordance are siblings, not nested
         * buttons — a `<button>` inside a `<button>` is invalid HTML and
         * also made the toggle's accessible name ambiguous with the copy
         * button's ("Copy <field>" is a substring match against the field
         * name), breaking `getByRole('button', { name })` queries. */}
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded(x => !x)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <Icon
            type={FLOW_ICONS.expand}
            size="xs"
            aria-hidden
            className={clsx('shrink-0 text-content-tertiary transition-transform', expanded && 'rotate-90')}
          />
          <Text size="xs" variant="tertiary">{fieldKey}</Text>
          <Text size="xs" variant="tertiary" className="ml-auto">{countLabel}</Text>
        </button>
        <CopyButton fieldKey={fieldKey} value={value} />
      </div>
      {expanded && (
        <div className={clsx('pl-4', !isLast && 'border-b-[0.5px] border-border-primary')}>
          <FactRows data={value} />
        </div>
      )}
    </div>
  );
}
